import React from 'react';
import { ScrollArea } from '../../components/ui/scroll-area';
import { StatusDot } from './StatusIndicators';
import { CERT_SCENARIOS } from './constants';

const ProcessingStatusBlock = ({ processingStatus }) => {
  if (!processingStatus) return null;
  const colorClass =
    processingStatus.status === 'success'
      ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
      : processingStatus.status === 'warning'
      ? 'bg-yellow-50 border-yellow-200 text-yellow-800'
      : 'bg-red-50 border-red-200 text-red-800';
  return (
    <div className="mx-4">
      <h5 className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1">
        Processing Result
      </h5>
      <div className={`p-2.5 rounded border text-xs ${colorClass}`}>
        <p className="font-medium">{processingStatus.message}</p>
        {processingStatus.errors?.map((err, i) => (
          <p key={err.code || i} className="mt-1 text-[10px] opacity-80">
            Error {err.code}: {err.text}
          </p>
        ))}
      </div>
    </div>
  );
};

const TransactionList = ({ results }) => {
  if (!results) return null;
  return (
    <div className="mx-4">
      <h5 className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1">
        Transactions ({results.length})
      </h5>
      <div className="space-y-1 max-h-32 overflow-y-auto">
        {results.map((r, i) => {
          const tx = r.result?.data?.data?.TransactionID;
          const label = r.client || r.employee || r.visit_id || `Record ${i + 1}`;
          const ok = r.result?.success;
          return (
            <div
              key={tx || label || i}
              className="flex items-center gap-2 text-xs"
            >
              <StatusDot status={ok ? 'success' : 'failed'} />
              <span className="text-slate-700 truncate flex-1">{label}</span>
              {tx && (
                <span className="font-mono text-[10px] text-slate-400 truncate">
                  {tx.slice(0, 12)}...
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const JsonBlock = ({ title, data, accentClass }) => {
  if (!data) return null;
  return (
    <div className="mx-4">
      <h5 className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1">
        {title}
      </h5>
      <ScrollArea className="h-36 rounded border bg-slate-950 p-3">
        <pre className={`text-[10px] ${accentClass} font-mono whitespace-pre-wrap`}>
          {JSON.stringify(data, null, 2)}
        </pre>
      </ScrollArea>
    </div>
  );
};

const DetailPanel = ({ scenario, result, processingStatus }) => {
  if (!scenario) {
    return (
      <div className="flex items-center justify-center h-48 text-sm text-slate-400">
        Select a scenario to view details
      </div>
    );
  }

  const sc = CERT_SCENARIOS.find((s) => s.id === scenario);
  const payload =
    result?.result?.payload_sent || result?.results?.[0]?.result?.payload_sent;
  const apiResponse = result?.result?.data || result?.results?.[0]?.result?.data;

  return (
    <div className="space-y-3">
      <div className="px-4 pt-3">
        <h4 className="font-semibold text-sm text-slate-800">{sc?.label}</h4>
        <p className="text-xs text-slate-500 mt-0.5">{sc?.description}</p>
      </div>
      <ProcessingStatusBlock processingStatus={processingStatus} />
      <TransactionList results={result?.results} />
      <JsonBlock title="Payload Sent" data={payload} accentClass="text-emerald-400" />
      {apiResponse && (
        <div className="pb-3">
          <JsonBlock title="API Response" data={apiResponse} accentClass="text-sky-400" />
        </div>
      )}
    </div>
  );
};

export default DetailPanel;
